import { JSX } from 'preact';

import {
  EditorProps,
  ViewerProps,
  RichProp,
  ImgProp,
} from 'client/courses/components/page-builder';
import { CtaButton } from 'client/courses/components/page-builder/cta-button';
import { ContainerWrapper } from 'client/courses/components/page-builder/container-wrapper';
import { createGenerateState } from './common-patterns';

import { Color } from 'shared/colors';
import { HeroBlockState, PaddingSize } from 'server/types';
import { RUZUKU_ASSETS_BASE_URL } from 'shared/consts';

export const type = 'calendar-hero';
export const name = 'Hero Section';

export const initialState: HeroBlockState = {
  layout: 'two-column',
  richText: `<h1>Transform Your Life with Expert Coaching</h1><p>Book a personalized session and unlock your potential with proven strategies tailored just for you.</p>`,
  callToAction: {
    text: 'Book Your Session',
    url: '/book-me',
  },
  imagePrompt: 'Smiling people',
  image: `${RUZUKU_ASSETS_BASE_URL}/smiling-people.jpg`,
};

export const generateState = createGenerateState(initialState);

export const initialBlockState = {
  type,
  bgcolor: Color.white,
  paddingt: 'lg' as PaddingSize,
  paddingb: 'lg' as PaddingSize,
};

export function MiniView(): JSX.Element {
  return (
    <span class="block py-4">
      <span class="w-4/6 mx-auto block text-center">
        <span class="block bg-gray-500 opacity-75 rounded-sm h-2 mb-2 mx-auto w-3/4"></span>
        <span class="block bg-gray-400 opacity-75 rounded-sm h-1 mb-3 mx-auto w-5/6"></span>
        <span class="bg-gray-600 opacity-75 rounded h-2 w-1/3 mx-auto block"></span>
      </span>
    </span>
  );
}

function View(props: EditorProps<HeroBlockState> | ViewerProps<HeroBlockState>): JSX.Element {
  const { state } = props;
  const layout = state.layout || 'two-column';

  if (layout === 'two-column') {
    return (
      <ContainerWrapper>
        <style>
          {`
            .sales-page .minidoc-content h1 {
              font-size: 4rem !important;
              line-height: 1.2 !important;
            }
            @container (max-width: 28rem) {
              .sales-page .minidoc-content h1 {
                font-size: 2.5rem !important;
                line-height: 1.2 !important;
              }
            }
          `}
        </style>
        <div class="grid @xl:grid-cols-2 gap-8 @xl:gap-16 items-start">
          <div class="order-2 lg:order-1 text-left">
            <RichProp
              {...props}
              path="richText"
              class="content-block-body @lg:text-2xl @sm:text-xl text-lg sales-page-hero"
            />
            <footer class="content-block-footer @lg:mt-8 @sm:mt-6 mt-4">
              <CtaButton {...props} path="callToAction" />
            </footer>
          </div>
          <div
            class={`order-1 lg:order-2 relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl`}
          >
            <ImgProp
              {...props}
              path="image"
              wrapperClass="w-full h-full"
              class="w-full h-full object-cover rounded-lg"
              overlayClass="rounded-2xl"
              width="600"
            />
          </div>
        </div>
      </ContainerWrapper>
    );
  }

  return (
    <ContainerWrapper class="text-center">
      <style>
        {`
          @container (max-width: 28rem) {
            .sales-page:not([data-theme-styles]) .minidoc-content h1 {
              font-size: 2.5rem !important;
              line-height: 1.2 !important;
            }
          }
        `}
      </style>
      <div class="max-w-4xl mx-auto">
        <RichProp
          {...props}
          path="richText"
          class="content-block-body @lg:text-2xl @sm:text-xl text-lg mx-auto sales-page-hero"
        />
        <footer class="content-block-footer @lg:mt-8 @sm:mt-6 mt-4">
          <CtaButton {...props} path="callToAction" />
        </footer>
      </div>
    </ContainerWrapper>
  );
}

export const Editor = View;
export const Viewer = View;
